import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router'

import { EMPTY_STRING } from '../../../constants/common'
import {
  getPageBrowserTabTitleWithEntityName,
  useDateLocale,
  usePageBrowserTabTitle,
  useTimeZone,
} from '../../../hooks'
import { useAsyncEntity } from '../../Entity'
import { MultipleViewTypeListActiveItemPreviewHeaderToggle } from '../../MultipleViewTypeList'
import { EntityPageProfileLayout } from '../../Page'
import { getMockViewingDetail } from '../../RentalPortalViewing'

import { QA_NAME_VIEWING_PROFILE_PAGE } from './RentalPortalViewingProfile.constants'
import { ViewingProfileContext } from './RentalPortalViewingProfile.context'
import {
  getViewingProfileData,
  getViewingProfilePreviewOpenFullPageProps,
  getViewingProfileTitle,
} from './RentalPortalViewingProfile.utils'
import { RentalPortalViewingProfileHeader } from './RentalPortalViewingProfileHeader'
import { RentalPortalViewingProfileOverview } from './RentalPortalViewingProfileOverview'
import { RentalPortalViewingProfilePreviewError } from './RentalPortalViewingProfilePreviewError'
import { RentalPortalViewingProfileTabs } from './RentalPortalViewingProfileTabs'
import { RentalPortalViewingProfileWidgetList } from './RentalPortalViewingProfileWidgetList'

function RentalPortalViewingProfile({
  ToggleButton = MultipleViewTypeListActiveItemPreviewHeaderToggle,
  isPreview,
  layoutProps,
  viewingId: viewingIdProp,
  onClosePreview,
  onEntityChange,
}) {
  const { viewingId: viewingIdFromParams } = useParams()
  const { t } = useTranslation()

  const locale = useDateLocale()
  const timezone = useTimeZone()

  const [modal, setModal] = useState(null)
  const [documentsReloadKey, setDocumentsReloadKey] = useState(0)
  const [noteEditValue, setNoteEditValue] = useState(null)

  function handleCancelNoteEdit() {
    setNoteEditValue(null)
  }

  const viewingId = viewingIdProp ?? viewingIdFromParams

  function getProfileData() {
    return getMockViewingDetail(viewingId)
  }

  const {
    entityData,
    handleEntityInit,
    handleEntityReload,
    isInitiated,
    isLoading,
    serverError,
  } = useAsyncEntity({ entityId: viewingId, getProfileData })

  function handleStartNoteEdit() {
    setNoteEditValue(entityData?.content ?? EMPTY_STRING)
  }

  usePageBrowserTabTitle({
    isEnabled: !isPreview,
    isLoading: !isInitiated,
    pageTitle: getPageBrowserTabTitleWithEntityName({
      name: getViewingProfileTitle({
        locale,
        t,
        timezone,
        viewingData: entityData,
      }),
      title: t('RentalPortal.ViewingProfileBrowserTabTitleKey'),
    }),
  })

  function handleCloseModal() {
    setModal(null)
  }

  function handleReloadDocuments() {
    setDocumentsReloadKey(prevReloadKey => prevReloadKey + 1)
  }

  async function handleReloadEntity() {
    onEntityChange?.()

    return handleEntityReload()
  }

  function handleClickRetry() {
    handleEntityInit()
  }

  const openFullPageActionsProps =
    isPreview && viewingId
      ? getViewingProfilePreviewOpenFullPageProps({
          Actions: null,
          t,
          viewingId,
          onClosePreview,
        })
      : null

  return isPreview && serverError ? (
    <RentalPortalViewingProfilePreviewError
      className={layoutProps?.className}
      openFullPageActionsProps={openFullPageActionsProps}
      onRetry={handleClickRetry}
    />
  ) : (
    <ViewingProfileContext
      value={{
        documentsReloadKey,
        handleCancelNoteEdit,
        handleCloseModal,
        handleEntityReload: handleReloadEntity,
        handleReloadDocuments,
        handleStartNoteEdit,
        isInitiated,
        isLoading,
        noteEditValue,
        setModal,
        setNoteEditValue,
        viewingData: getViewingProfileData({
          isPreview,
          timezone,
          viewingData: entityData,
        }),
      }}
    >
      <EntityPageProfileLayout
        {...layoutProps}
        Footer={isPreview ? null : undefined}
        Header={RentalPortalViewingProfileHeader}
        headerProps={{
          ToggleButton,
          isPreview,
          openFullPageActionsProps,
        }}
        qaName={QA_NAME_VIEWING_PROFILE_PAGE}
        serverError={serverError}
        widgets={isPreview ? null : <RentalPortalViewingProfileWidgetList />}
      >
        {isPreview ? (
          <RentalPortalViewingProfileOverview />
        ) : (
          <RentalPortalViewingProfileTabs />
        )}
        {modal}
      </EntityPageProfileLayout>
    </ViewingProfileContext>
  )
}

export default RentalPortalViewingProfile
